import * as Sentry from '@sentry/nuxt';
import { safeRandomUUID } from '~/utils/safeRandomUUID';
import {
  sanitizeSentryHeaders,
  sanitizeSentryRequest,
  sanitizeSentryValue,
} from '~/utils/sentryMonitoring';
import { isSessionExpiredResponse } from '~/utils/authSessionExpiry';
import { reportClientApiNetworkFailure } from '~/utils/telegramErrorMonitor';

export interface UseFetchApiOptions {
  key?: string;
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  query?: { [prop: string]: unknown };
  params?: { [prop: string]: unknown };
  body?: RequestInit['body'] | Record<string, any>;
  headers?: [string, string][] | Record<string, string> | {};
  baseURL?: string;
  pick?: string[];
  rawData?: boolean;
  lazy?: boolean;
  credentials?: RequestCredentials;
  responseType?: 'json' | 'text' | 'blob' | 'arrayBuffer';
  retry?: number | false;
  retryDelay?: number;
  timeout?: number;
}

const API_BASE_URL = '/api';
const DEFAULT_SAFE_METHOD_RETRIES = 2;
const DEFAULT_SAFE_METHOD_RETRY_DELAY_MS = 300;
const SAFE_RETRY_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);

const requestMethod = (
  options: UseFetchApiOptions | Record<string, any> | undefined,
) => String(options?.method ?? 'GET').toUpperCase();

const apiResilienceOptions = (
  options: UseFetchApiOptions | Record<string, any> | undefined,
) => {
  const method = requestMethod(options);

  const resilienceOptions: {
    retry: number | false;
    retryDelay: number;
    timeout?: number;
  } = {
    retry:
      options?.retry ??
      (SAFE_RETRY_METHODS.has(method) ? DEFAULT_SAFE_METHOD_RETRIES : 0),
    retryDelay: options?.retryDelay ?? DEFAULT_SAFE_METHOD_RETRY_DELAY_MS,
  };

  if (options?.timeout !== undefined) {
    resilienceOptions.timeout = options.timeout;
  }

  return resilienceOptions;
};

const getFetchApiMessage = (
  response: any,
  fallback = 'Error',
  translate?: (key: string) => string,
) => {
  if (Number(response?.status ?? response?.statusCode) === 403) {
    return (
      translate?.('youDontHavePermissionForPage') ||
      response?.statusText ||
      fallback
    );
  }

  return (
    response?._data?.data?.message ||
    response?._data?.message ||
    response?.data?.message ||
    response?.statusText ||
    fallback
  );
};

const handleFetchApiError = (
  response: any,
  toast: ReturnType<typeof useToast>,
  translate?: (key: string) => string,
) => {
  const message = getFetchApiMessage(response, 'Error', translate);

  toast.add({
    title: message,
    timeout: 3000,
    color: 'red',
  });

  if (isSessionExpiredResponse(response)) {
    void useLogoutFromAuth();
  }

  if ([404, 403, 500].includes(response?.status)) {
    throw createError({
      statusCode: response?.status,
      message,
    });
  }
};

const captureFetchApiFailure = (
  url: string,
  errorOrResponse: any,
  options: UseFetchApiOptions | Record<string, any> | undefined,
  source: string,
) => {
  try {
    const response = errorOrResponse?.response ?? errorOrResponse;
    const method = requestMethod(options);
    const status = response?.status ?? response?.statusCode;
    const message = getFetchApiMessage(response, 'API request failed');
    const sentryError =
      errorOrResponse instanceof Error ? errorOrResponse : new Error(message);
    const apiBaseURL =
      typeof options?.baseURL === 'string' && options.baseURL !== ''
        ? options.baseURL
        : API_BASE_URL;

    Sentry.addBreadcrumb({
      category: 'http',
      type: 'http',
      level: 'error',
      message: `${method} ${url}`,
      data: {
        url,
        method,
        status_code: status,
      },
    });

    Sentry.withScope((scope) => {
      scope.setTag('monitoring.source', source);
      scope.setTag('http.method', method);

      if (status) {
        scope.setTag('http.status_code', String(status));
      }

      scope.setContext('api.request', {
        url,
        method,
        query: sanitizeSentryValue(
          'query',
          options?.query ?? options?.params ?? {},
        ),
        headers: sanitizeSentryHeaders(
          (options?.headers ?? {}) as Record<string, any>,
        ),
        body: sanitizeSentryValue('body', options?.body),
      });

      scope.setContext(
        'api.response',
        sanitizeSentryRequest({
          status,
          statusText: response?.statusText,
          url: response?.url,
          data: response?._data ?? response?.data,
          headers: response?.headers,
        }),
      );

      Sentry.captureException(sentryError);
    });

    void reportClientApiNetworkFailure(url, errorOrResponse, {
      source,
      method,
      baseURL: apiBaseURL,
      query: options?.query ?? options?.params,
      retry: options?.retry,
      timeout: options?.timeout,
    });
  } catch {
    // Monitoring must never alter application error handling.
  }
};

export async function fetchApi<T = any>(
  url: string,
  options?: UseFetchApiOptions,
): Promise<T> {
  const headers_cookie = useRequestHeaders(['cookie']) as HeadersInit;
  const i18n = useNuxtApp().$i18n;
  const { locale } = i18n;
  const translate =
    typeof i18n.t === 'function' ? i18n.t.bind(i18n) : undefined;
  const toast = useToast();

  const {
    headers: userHeaders,
    key: _key,
    lazy: _lazy,
    pick: _pick,
    rawData: _rawData,
    params,
    query,
    responseType,
    credentials,
    ...restOptions
  } = options ?? {};

  try {
    const data = await $fetch<T>(url, {
      baseURL: API_BASE_URL,
      responseType: responseType ?? 'json',
      credentials: credentials ?? 'include',
      ...apiResilienceOptions(options),
      headers: Object.assign(
        { 'Content-Language': locale.value },
        userHeaders,
        headers_cookie,
      ),
      query: query ?? params,
      ...restOptions,
    });

    if (isSessionExpiredResponse(data)) {
      void useLogoutFromAuth();
    }

    return data;
  } catch (error: any) {
    captureFetchApiFailure(url, error, options, 'fetchApi');
    handleFetchApiError(error?.response ?? error, toast, translate);
    throw error;
  }
}

export default function useFetchApi(
  url: string,
  options?: UseFetchApiOptions,
): any {
  // important: this next line to set cookie after refresh page in the browser
  const headers_cookie = useRequestHeaders(['cookie']) as HeadersInit;
  const i18n = useNuxtApp().$i18n;
  const { locale } = i18n;
  const translate =
    typeof i18n.t === 'function' ? i18n.t.bind(i18n) : undefined;
  const toast = useToast();

  const { headers: userHeaders, ...restOptions } = options ?? {};

  const optionsWithDefault = {
    lazy: true,
    baseURL: API_BASE_URL,
    responseType: restOptions.responseType ?? 'json',
    ...apiResilienceOptions(restOptions),
    headers: Object.assign(
      { 'Content-Language': locale.value },
      userHeaders,
      headers_cookie,
    ),
    ...restOptions,
  };

  return useFetch(url, {
    onRequest({ options }) {
      Object.assign(options, optionsWithDefault);
    },
    onRequestError({ error }) {
      captureFetchApiFailure(url, error, optionsWithDefault, 'useFetchApi');
      console.error({ error });
    },
    onResponse({ options, response }) {
      Object.assign(options, optionsWithDefault);

      if (isSessionExpiredResponse(response)) {
        void useLogoutFromAuth();
      }
    },
    onResponseError({ response }) {
      captureFetchApiFailure(url, response, optionsWithDefault, 'useFetchApi');
      handleFetchApiError(response, toast, translate);
    },
    params: options?.params,
    key: options?.key ?? safeRandomUUID('fetch'),
  });
}
